import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar, ArrowRight, Sparkles, Globe, Users, Building2, Star, ShieldCheck, MapPin, Clock, Tag, Crown, ChevronLeft, ChevronRight, Play } from 'lucide-react';
// AI Chat disabled - will reconnect when more shows/exhibitors available
// import AIChat from '../components/AIChat';
import SEOHead from '../components/SEOHead';
import { useCurrency } from '../contexts/CurrencyContext';
import { generateOrganizationSchema } from '../utils/seo';
import AutoTranslatedText from '../components/AutoTranslatedText';
import OptimizedImage from '../components/OptimizedImage';
import VideoPlayer from '../components/VideoPlayer';
import { parseLocalDate } from '../utils/dateUtils';

const HomePage = () => {
  const [liveEvents, setLiveEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [featuredExhibitors, setFeaturedExhibitors] = useState([]);
  const [eliteExhibitors, setEliteExhibitors] = useState([]);
  const [eliteBannerIndex, setEliteBannerIndex] = useState(0);
  const [siteSettings, setSiteSettings] = useState(null);
  const { user } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const currentLang = i18n.language || 'en';

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-rotate elite banner every 5 seconds
  useEffect(() => {
    if (eliteExhibitors.length <= 1) return;
    const interval = setInterval(() => {
      setEliteBannerIndex((prev) => (prev + 1) % eliteExhibitors.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [eliteExhibitors.length]);

  const fetchData = async () => {
    try {
      const [eventsRes, featuredRes, settingsRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/featured-exhibitors`),
        axios.get(`${API}/settings`).catch(() => ({ data: {} })),
      ]);
      // Separate live (including registration_open) and upcoming events
      setLiveEvents(eventsRes.data.filter(e => e.status === 'live' || e.status === 'registration_open'));
      setUpcomingEvents(eventsRes.data.filter(e => e.status === 'upcoming'));
      setFeaturedExhibitors(featuredRes.data);
      setSiteSettings(settingsRes.data);
      // Filter Elite tier exhibitors for rotating banner
      setEliteExhibitors(featuredRes.data.filter(b => b.tier === 'elite'));
    } catch (error) {
      console.error('Failed to fetch data', error);
    }
  };

  // Navigate elite banner
  const nextEliteBanner = () => {
    setEliteBannerIndex((prev) => (prev + 1) % eliteExhibitors.length);
  };
  const prevEliteBanner = () => {
    setEliteBannerIndex((prev) => (prev - 1 + eliteExhibitors.length) % eliteExhibitors.length);
  };

  // Check if early bird discount is active for an event
  const isEarlyBirdActive = (event) => {
    if (!event.early_bird_enabled) return false;
    try {
      const startDate = parseLocalDate(event.start_date);
      const daysBeforeMs = (event.early_bird_days_before || 30) * 24 * 60 * 60 * 1000;
      const deadline = new Date(startDate.getTime() - daysBeforeMs);
      return new Date() < deadline;
    } catch {
      return false;
    }
  };

  const getTierBadge = (tier) => {
    if (tier === 'elite') {
      return (
        <Badge className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-0 shadow-md">
          <Crown className="h-3 w-3 mr-1 fill-yellow-300 text-yellow-300" />
          {t('event_detail.tier_elite')}
        </Badge>
      );
    }
    return (
      <Badge className="bg-orange-100 text-orange-700 border border-orange-200">
        <Star className="h-3 w-3 mr-1 fill-orange-500" />
        {t('event_detail.tier_premium')}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* SEO Head - Let SEOHead handle translations internally */}
      <SEOHead 
        structuredData={generateOrganizationSchema()}
      />

      {/* Elite Exhibitors Rotating Banner */}
      {eliteExhibitors.length > 0 && (
        <div className="bg-gradient-to-r from-purple-900 via-indigo-900 to-purple-900 text-white py-4" data-testid="elite-banner-carousel">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center gap-4">
              {/* Previous Button */}
              {eliteExhibitors.length > 1 && (
                <button 
                  onClick={prevEliteBanner}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  aria-label="Previous elite exhibitor"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
              )}
              
              {/* Banner Content */}
              <Link 
                to={user ? `/booth/${eliteExhibitors[eliteBannerIndex]?.id}` : '/auth'}
                className="flex items-center gap-4 hover:opacity-90 transition-opacity flex-1 max-w-4xl"
              >
                <div className="flex items-center gap-2 shrink-0">
                  <Crown className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-purple-200">{t('home.elite_sponsor') || 'Elite Sponsor'}</span>
                </div>
                
                {/* Company Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-lg truncate">{eliteExhibitors[eliteBannerIndex]?.company_name}</p>
                  {eliteExhibitors[eliteBannerIndex]?.slogan && (
                    <p className="text-sm text-purple-200 truncate">{eliteExhibitors[eliteBannerIndex]?.slogan}</p>
                  )}
                </div>
                
                {/* Banner Image Preview */}
                {eliteExhibitors[eliteBannerIndex]?.banner_url && (
                  <div className="hidden md:block h-12 w-40 overflow-hidden rounded shrink-0">
                    <OptimizedImage 
                      src={eliteExhibitors[eliteBannerIndex].banner_url}
                      alt=""
                      className="h-full w-full"
                      objectFit="cover"
                    />
                  </div>
                )}
                
                <Button size="sm" variant="secondary" className="shrink-0">
                  {t('home.visit_booth') || 'Visit Booth'} <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </Link>
              
              {/* Next Button */}
              {eliteExhibitors.length > 1 && (
                <button 
                  onClick={nextEliteBanner}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  aria-label="Next elite exhibitor"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              )}
            </div>
            
            {/* Dots Indicator */}
            {eliteExhibitors.length > 1 && (
              <div className="flex justify-center gap-2 mt-2">
                {eliteExhibitors.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setEliteBannerIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-colors ${idx === eliteBannerIndex ? 'bg-white' : 'bg-white/30'}`}
                    aria-label={`Go to elite exhibitor ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hero Section - Bento Grid */}
      <div className="relative bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-4 md:pt-8 md:pb-6">
          {/* Hero Banner */}
          <div className="relative w-full mb-6 rounded-lg overflow-hidden shadow-lg">
            <img 
              src="/tradehub_banner.png" 
              alt="TradeHub - Your Unified Platform for Efficient Sourcing & Selling" 
              className="w-full h-auto object-cover"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Main Hero Content */}
            <div className="md:col-span-8 bg-white border border-slate-200 rounded-md p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-2 mb-4">
                <Sparkles className="h-5 w-5 text-accent" />
                <span className="text-xs uppercase tracking-widest text-accent font-medium">{t('home.live_virtual_exhibits')}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 tracking-tight">
                {t('home.hero_title')}
              </h1>
              <p className="text-base text-slate-600 mb-4 max-w-2xl">
                {t('home.hero_subtitle')}
              </p>
              
              {/* Introduction Video - Language-aware */}
              {(() => {
                // Get video URL based on current language (fallback to English/default)
                const getVideoForLanguage = () => {
                  if (!siteSettings) return null;
                  const lang = i18n.language;
                  if (lang === 'fr' && siteSettings.intro_video_url_fr) return siteSettings.intro_video_url_fr;
                  if (lang === 'es' && siteSettings.intro_video_url_es) return siteSettings.intro_video_url_es;
                  if (lang === 'de' && siteSettings.intro_video_url_de) return siteSettings.intro_video_url_de;
                  return siteSettings.intro_video_url; // Default (English)
                };
                
                const videoUrl = getVideoForLanguage();
                if (!videoUrl) return null;
                
                // Check if showing localized video or fallback
                const hasLocalizedVideo = 
                  (i18n.language === 'fr' && siteSettings?.intro_video_url_fr) ||
                  (i18n.language === 'es' && siteSettings?.intro_video_url_es) ||
                  (i18n.language === 'de' && siteSettings?.intro_video_url_de) ||
                  i18n.language === 'en';
                
                return (
                  <div className="mb-4">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <Play className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium text-slate-700">{t('home.watch_intro') || 'Watch Introduction'}</span>
                      </div>
                      {!hasLocalizedVideo && i18n.language !== 'en' && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                          Video in English
                        </span>
                      )}
                    </div>
                    <VideoPlayer 
                      src={videoUrl}
                      poster={siteSettings?.intro_video_poster}
                      title={t('home.intro_video_title') || 'Virtual Trade Shows Introduction'}
                      className="shadow-lg w-full"
                    />
                  </div>
                );
              })()}
              
              <div className="flex flex-wrap gap-4">
                <Link to="/events">
                  <Button size="lg" className="active:scale-95 transition-transform" data-testid="home-browse-events-btn">
                    {t('home.browse_events')} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Side Card - Incoming/Upcoming Events */}
            <div className="md:col-span-4 space-y-3">
              <div className="bg-primary text-white border border-primary rounded-md p-4 shadow-sm">
                <Clock className="h-6 w-6 mb-2" />
                <h3 className="text-lg font-semibold mb-1">{t('home.incoming_exhibits')}</h3>
                <p className="text-sm opacity-90 mb-3">{t('home.upcoming_desc')}</p>
                <Link to="/calendar">
                  <Button variant="secondary" size="sm" data-testid="home-view-calendar-btn">
                    {t('home.view_calendar')}
                  </Button>
                </Link>
              </div>

              {upcomingEvents.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-md p-4">
                  <p className="text-sm text-slate-500 text-center">{t('home.no_upcoming')}</p>
                </div>
              ) : (
                <div className="bg-white border border-slate-200 rounded-md p-4">
                  <p className="text-xs text-slate-500 mb-2">{upcomingEvents.length} {upcomingEvents.length > 1 ? t('home.upcoming_exhibits') : t('home.upcoming_exhibit')}</p>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {upcomingEvents.slice(0, 5).map((event) => (
                      <div
                        key={event.id}
                        className="border-b border-slate-100 last:border-0 pb-3 last:pb-0"
                        data-testid={`home-upcoming-event-${event.id}`}
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {t('home.upcoming')}
                          </span>
                        </div>
                        <h4 className="font-semibold text-slate-900 text-sm">
                          <AutoTranslatedText text={event.name} translations={event.name_translations} showLoading={false} />
                        </h4>
                        <p className="text-xs text-slate-500 mt-1">
                          <Calendar className="inline h-3 w-3 mr-1" />
                          {parseLocalDate(event.start_date).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Live Exhibits Horizontal Scroll */}
      {liveEvents.length > 0 && (
        <div className="bg-white border-b border-slate-200 pt-4 pb-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">{t('home.live_exhibits')}</h2>
                <p className="text-slate-600 text-sm">{t('home.browse_active_tradeshows')}</p>
              </div>
              <Link to="/events">
                <Button variant="outline" size="sm">
                  {t('home.show_all')} <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {liveEvents.map((event) => (
                <div
                  key={event.id}
                  className="flex-shrink-0 w-72 bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {/* Event Banner */}
                  <div className="aspect-video bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center overflow-hidden relative">
                    {event.banner_url ? (
                      <OptimizedImage 
                        src={event.banner_url}
                        alt={event.name}
                        className="w-full h-full"
                        objectFit="cover"
                      />
                    ) : (
                      <Calendar className="h-12 w-12 text-primary" />
                    )}
                    {/* Early Bird Badge */}
                    {isEarlyBirdActive(event) && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg">
                        <Tag className="h-3 w-3" />
                        {event.early_bird_discount_percent}% {t('home.off')}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    {event.status === 'live' ? (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5 animate-pulse"></span>
                        {t('home.live_now')}
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded flex items-center">
                        <span className="w-2 h-2 bg-amber-500 rounded-full mr-1.5"></span>
                        {t('home.registration_open')}
                      </span>
                    )}
                    {isEarlyBirdActive(event) && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                        {t('home.early_bird')}
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">
                    <AutoTranslatedText text={event.name} translations={event.name_translations} showLoading={false} />
                  </h3>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                    <AutoTranslatedText text={event.description} translations={event.description_translations} showLoading={false} />
                  </p>
                  <div className="flex items-center text-xs text-slate-500 mb-2">
                    <Calendar className="h-3 w-3 mr-1" />
                    {parseLocalDate(event.start_date).toLocaleDateString()} - {parseLocalDate(event.end_date).toLocaleDateString()}
                  </div>
                  {/* Booth fees only visible to admins and vendors */}
                  {(user?.role === 'admin' || user?.role === 'seller') && (
                    <div className="text-xs text-slate-500">
                      <span className="font-medium">{t('home.booth_fees')}:</span> {formatPrice(event.booth_fees?.basic || 500)} - {formatPrice(event.booth_fees?.elite || 3000)}
                    </div>
                  )}
                  {event.buyer_fee > 0 && (
                    <div className="text-xs text-slate-500 mb-3">
                      <span className="font-medium">{t('home.entry_fee')}:</span> {isEarlyBirdActive(event) ? (
                        <>
                          <span className="line-through">{formatPrice(event.buyer_fee)}</span>
                          <span className="ml-1 text-green-600 font-semibold">
                            {formatPrice(event.buyer_fee * (1 - event.early_bird_discount_percent / 100))}
                          </span>
                        </>
                      ) : (
                        formatPrice(event.buyer_fee)
                      )}
                    </div>
                  )}
                  {/* Registration buttons for non-logged-in users */}
                  {!user && (
                    <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-slate-100">
                      {event.status === 'live' && (
                        <Link to="/auth" className="w-full">
                          <Button size="sm" className="w-full text-xs">
                            {t('home.register_attendee')}
                          </Button>
                        </Link>
                      )}
                      {event.status === 'registration_open' && (
                        <>
                          <Link to="/auth" className="w-full">
                            <Button size="sm" variant="outline" className="w-full text-xs">
                              {t('home.register_vendor')}
                            </Button>
                          </Link>
                          <Link to="/auth" className="w-full">
                            <Button size="sm" className="w-full text-xs">
                              {t('home.register_attendee')}
                            </Button>
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                  {/* Show Details Button */}
                  <Link to={`/events/${event.id}`} className="block mt-3">
                    <Button size="sm" variant="outline" className="w-full text-xs">
                      {t('home.show_details')} <ArrowRight className="ml-1 h-3 w-3" />
                    </Button>
                  </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Featured Exhibitors Banner */}
      {featuredExhibitors.length > 0 && (
        <div className="bg-gradient-to-b from-white to-slate-50 border-y border-slate-200 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <div className="text-center flex-1">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                  <h2 className="text-2xl font-bold text-slate-900">{t('home.featured_exhibitors')}</h2>
                  <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                </div>
                <p className="text-slate-600 text-sm">{t('home.verified_premium_partners')}</p>
              </div>
              <Link to="/exhibitors">
                <Button variant="outline" size="sm">
                  {t('home.show_all')} <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredExhibitors.map((booth) => (
                <Link
                  key={booth.id}
                  to={user ? `/booth/${booth.id}` : '/auth'}
                  className="group"
                  data-testid={`home-featured-exhibitor-${booth.id}`}
                >
                  <div className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-xl transition-all group-hover:border-primary h-full flex flex-col">
                    {/* Banner/Logo Area */}
                    <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center relative overflow-hidden">
                      {booth.banner_url ? (
                        <OptimizedImage 
                          src={booth.banner_url} 
                          alt={booth.company_name} 
                          className="w-full h-full" 
                          objectFit="cover"
                        />
                      ) : booth.logo_url ? (
                        <OptimizedImage 
                          src={booth.logo_url} 
                          alt={booth.company_name} 
                          className="max-w-full max-h-full p-4" 
                          objectFit="contain"
                        />
                      ) : (
                        <Building2 className="h-16 w-16 text-slate-300" />
                      )}
                      {/* Tier Badge */}
                      <div className="absolute top-3 right-3">
                        {getTierBadge(booth.tier)}
                      </div>
                      {/* Verified Badge */}
                      <div className="absolute top-3 left-3">
                        <Badge className="bg-green-100 text-green-700 border border-green-200">
                          <ShieldCheck className="h-3 w-3 mr-1" />
                          {t('home.verified')}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-4 flex-1 flex flex-col">
                      <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-primary transition-colors">
                        {booth.company_name}
                      </h3>
                      
                      {booth.slogan && (
                        <p className="text-sm text-slate-500 italic mb-2 line-clamp-1">
                          "<AutoTranslatedText text={booth.slogan} translations={booth.slogan_translations} showLoading={false} />"
                        </p>
                      )}
                      
                      {booth.description && (
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3 flex-1">
                          <AutoTranslatedText text={booth.description} translations={booth.description_translations} showLoading={false} />
                        </p>
                      )}
                      
                      {/* Seller Company Info */}
                      {booth.seller?.company_info && (
                        <div className="text-xs text-slate-500 mb-3 flex items-center">
                          <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="truncate">
                            {booth.seller.company_info.city}, {booth.seller.company_info.country}
                          </span>
                          {booth.seller.company_info.industry && (
                            <span className="ml-2 px-1.5 py-0.5 bg-slate-100 rounded text-xs">
                              {booth.seller.company_info.industry}
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* Event Info */}
                      {booth.event && (
                        <div className="mt-auto pt-3 border-t border-slate-100">
                          <div className="flex items-center text-xs text-slate-500">
                            <Calendar className="h-3 w-3 mr-1.5 flex-shrink-0" />
                            <span className="truncate">{t('home.at')}: <strong className="text-slate-700">
                              <AutoTranslatedText text={booth.event.name} translations={booth.event.name_translations} showLoading={false} />
                            </strong></span>
                          </div>
                          <div className="flex items-center mt-1">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5 animate-pulse"></span>
                            <span className="text-xs text-green-600 font-medium">{t('home.live')}</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Login prompt for non-logged users */}
                      {!user && (
                        <div className="mt-3 pt-2 border-t border-slate-100">
                          <p className="text-xs text-primary font-medium">{t('home.sign_in_to_view')} →</p>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Benefits Section */}
      <div className="py-10 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('home.why_virtual')}</h2>
            <p className="text-slate-600 text-sm max-w-2xl mx-auto">
              {t('home.why_virtual_desc')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white border border-slate-200 rounded-md p-5 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                <Globe className="h-5 w-5 text-primary" />
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-2">{t('home.global_reach')}</h3>
              <p className="text-sm text-slate-600">
                {t('home.global_reach_desc')}
              </p>
            </div>
            
            <div className="bg-white border border-slate-200 rounded-md p-5 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center mb-3">
                <Users className="h-5 w-5 text-accent" />
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-2">{t('home.live_interactions')}</h3>
              <p className="text-sm text-slate-600">
                {t('home.live_interactions_desc')}
              </p>
            </div>
            
            <div className="bg-white border border-slate-200 rounded-md p-5 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                <Building2 className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-2">{t('home.virtual_booths')}</h3>
              <p className="text-sm text-slate-600">
                {t('home.virtual_booths_desc')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* AI Chat Assistant */}
      {/* AI Chat disabled - will reconnect when more shows/exhibitors available */}
    </div>
  );
};

export default HomePage;
